import React, { useMemo, useState } from "react";
import { ExternalLink, AlertTriangle } from "lucide-react";

/**
 * TradingView Advanced Chart — iframe embed.
 *
 * We use the iframe URL (rather than the JS-script-injection embed) so that
 * cross-origin script errors stay sandboxed inside the iframe and don't
 * trigger the CRA dev-mode error overlay. The widget UX is identical:
 * 100+ indicators, drawing-tool sidebar, multi-timeframe, replay, etc.
 *
 * Docs (iframe params): https://www.tradingview.com/widget/advanced-chart/
 *
 * Per TV TOS we render the attribution link below the chart.
 */
// Free-tier TradingView widget cannot stream actual futures contracts
// (CME_MINI:ES1!, NYMEX:CL1!, etc.) — those require a logged-in TV account
// and trigger a "this chart is only available on tradingview.com" overlay.
// Map every futures symbol to its closest *free-tier* cash-index / CFD /
// spot equivalent so the widget renders the price action without that wall.
// Where a perfect proxy doesn't exist (e.g. /ZN US 10Y note), we use the
// underlying yield or rate index.
const _SYMBOL_MAP = {
  SPX: "FOREXCOM:SPXUSD",
  NDX: "FOREXCOM:NSXUSD",
  RUT: "CAPITALCOM:US2000",
  VIX: "CAPITALCOM:VIX",
  DJX: "FOREXCOM:DJI",
  // Equity index futures → cash index proxies (continuously tradeable, free)
  "/ES": "FOREXCOM:SPXUSD",
  "/NQ": "FOREXCOM:NSXUSD",
  "/YM": "FOREXCOM:DJI",
  "/RTY": "CAPITALCOM:US2000",
  // Energy → free CFD / spot tickers
  "/CL": "TVC:USOIL",
  "/MCL": "TVC:USOIL",
  "/NG": "TVC:NATURALGAS",
  // Metals → spot proxies on OANDA (free)
  "/GC": "OANDA:XAUUSD",
  "/SI": "OANDA:XAGUSD",
  "/HG": "TVC:COPPER",
  // Rates → yield indices (free)
  "/ZN": "TVC:US10Y",
  "/ZB": "TVC:US30Y",
  "/ZT": "TVC:US02Y",
  "/ZF": "TVC:US05Y",
  // Crypto futures → free spot exchange
  "/BTC": "BINANCE:BTCUSDT",
  // FX futures → free spot pairs
  "/6E": "FX:EURUSD",
  "/6J": "OANDA:USDJPY",
};

const normalizeSymbol = (sym) => {
  if (!sym) return "AAPL";
  const upper = String(sym).toUpperCase().trim();
  return _SYMBOL_MAP[upper] || upper;
};

// Human-friendly proxy descriptions for futures tickers we're remapping.
// Surfaced to the UI so users understand the chart shows the cash / spot
// equivalent instead of the actual futures contract (which TV's free widget
// can't stream).
const _PROXY_LABELS = {
  "/ES":  { label: "S&P 500 cash",          short: "S&P 500" },
  "/NQ":  { label: "Nasdaq 100 cash",       short: "Nasdaq 100" },
  "/YM":  { label: "Dow Jones cash",        short: "Dow" },
  "/RTY": { label: "Russell 2000 cash",     short: "Russell 2K" },
  "/CL":  { label: "WTI crude CFD",         short: "WTI crude" },
  "/MCL": { label: "WTI crude CFD",         short: "WTI crude" },
  "/NG":  { label: "Natural gas CFD",       short: "Nat gas" },
  "/GC":  { label: "Gold spot (XAU/USD)",   short: "Gold spot" },
  "/SI":  { label: "Silver spot (XAG/USD)", short: "Silver spot" },
  "/HG":  { label: "Copper CFD",            short: "Copper" },
  "/ZN":  { label: "US 10Y Treasury yield", short: "US 10Y" },
  "/ZB":  { label: "US 30Y Treasury yield", short: "US 30Y" },
  "/ZT":  { label: "US 2Y Treasury yield",  short: "US 2Y" },
  "/ZF":  { label: "US 5Y Treasury yield",  short: "US 5Y" },
  "/BTC": { label: "BTC/USDT spot",         short: "BTC spot" },
  "/6E":  { label: "EUR/USD spot",          short: "EUR/USD" },
  "/6J":  { label: "USD/JPY spot",          short: "USD/JPY" },
};

// Public helper — consumers (e.g. FuturesChartPanel) call this to render a
// "/ES → S&P 500 cash (proxy)" sub-label so the symbol-swap is transparent.
// Returns null when the symbol is a plain equity / index we don't proxy.
export const getTradingViewProxyInfo = (sym) => {
  if (!sym) return null;
  const upper = String(sym).toUpperCase().trim();
  const meta = _PROXY_LABELS[upper];
  if (!meta) return null;
  return { ...meta, tv_symbol: _SYMBOL_MAP[upper] };
};

const TradingViewWidget = ({
  symbol = "AAPL",
  interval = "D",
  theme = "dark",
  height = 600,
  studies = ["MASimple", "MACD", "RSI", "Volume"],
  className = "",
}) => {
  const [errored, setErrored] = useState(false);
  const tvSymbol = normalizeSymbol(symbol);

  // Build the iframe URL. We pass theme + interval + symbol + studies so
  // the chart boots with the user's chosen indicator stack loaded.
  const iframeSrc = useMemo(() => {
    const studyParam = (studies || [])
      .map((s) => `${s}@tv-basicstudies`)
      .join(",");
    const params = new URLSearchParams({
      symbol: tvSymbol,
      interval,
      hidesidetoolbar: "0",
      hidetoptoolbar: "0",
      symboledit: "1",
      saveimage: "1",
      toolbarbg: "0a0a0c",
      studies: studyParam,
      theme,
      style: "1",
      timezone: "America/New_York",
      withdateranges: "1",
      hideideas: "1",
      studies_overrides: "{}",
      overrides: "{}",
      enabled_features: "[]",
      disabled_features: "[]",
      locale: "en",
      utm_source: "gammatrade.app",
      utm_medium: "widget_new",
      utm_campaign: "chart",
    });
    return `https://s.tradingview.com/widgetembed/?${params.toString()}`;
  }, [tvSymbol, interval, theme, studies]);

  if (errored) {
    return (
      <div
        className={"flex flex-col items-center justify-center text-center p-12 border border-white/10 rounded-xl bg-[#0a0a0c] " + className}
        data-testid="tradingview-error"
      >
        <AlertTriangle className="w-6 h-6 text-[#FFD60A] mb-3" />
        <p className="text-white/65 text-sm">
          Couldn&apos;t load the TradingView chart. Refresh, or use the compact
          chart toggle.
        </p>
      </div>
    );
  }

  return (
    <div
      className={"tradingview-widget-container relative " + className}
      data-testid="tradingview-widget"
    >
      <iframe
        key={iframeSrc /* re-mount on symbol change */}
        src={iframeSrc}
        title={`TradingView chart — ${tvSymbol}`}
        width="100%"
        height={height}
        frameBorder="0"
        allowTransparency
        scrolling="no"
        allowFullScreen
        onError={() => setErrored(true)}
        style={{ display: "block", borderRadius: 10 }}
      />
      <div className="tradingview-widget-copyright text-[10px] text-white/35 mt-1 px-1">
        <a
          href={`https://www.tradingview.com/symbols/${encodeURIComponent(tvSymbol)}/`}
          rel="noopener nofollow noreferrer"
          target="_blank"
          className="hover:text-white/65 inline-flex items-center gap-1"
          data-testid="tradingview-attribution"
        >
          <span className="text-[#3FE5DC]/85">{tvSymbol}</span> chart by TradingView
          <ExternalLink className="w-2.5 h-2.5" />
        </a>
      </div>
    </div>
  );
};

export default TradingViewWidget;
